<template>
  <div v-if="updateRequired" class="force-update-overlay" role="alertdialog" aria-modal="true" aria-labelledby="force-update-title">
    <div class="force-update-card">
      <img :src="logoSrc" alt="Kiey" class="force-update-logo" />
      <h2 id="force-update-title" class="force-update-title">Update Required</h2>
      <p class="force-update-text">
        A new version of Kiey is available. Please update to continue —
        you must be on the latest version to keep using the app.
      </p>
      <p class="force-update-meta">
        You are on <strong>v{{ localVersion || 'unknown' }}</strong>.
        Latest is <strong>v{{ requiredVersion || 'unknown' }}</strong>.
      </p>
      <button class="force-update-btn" :disabled="reloading" @click="reload">
        <span v-if="!reloading">Update Now</span>
        <span v-else>Updating…</span>
      </button>
    </div>
  </div>
</template>

<script>
import { firestore } from '@/config/firebase';
import { isUpdateRequired } from '@/util/version';

export default {
  name: 'ForceUpdateOverlay',
  data() {
    return {
      localVersion: process.env.VUE_APP_VERSION || '',
      requiredVersion: '',
      reloading: false,
      unsubscribe: null,
      logoSrc: '',
      // Set true when a prior "Update Now" reload came back on the SAME local
      // version — i.e. the update is impossible (the floor is armed above what's
      // actually deployed). We then fail open so the user is never trapped.
      escapedStuck: false
    };
  },
  computed: {
    // The public kiey.com/my-market page is built from this same Vue source but
    // with VUE_APP_PUBLIC_PATH=/my-market-app/ (see Makefile target
    // $(KIEY_MY_MARKET_DIR)). That bundle ships on the kiey.com marketing cadence
    // — out of lockstep with the app's min_version floor — so it must NEVER show
    // the force-update gate, or a stale public bundle loops forever on a
    // prospect-facing page. This build-time flag is the only signal that
    // distinguishes the my-market bundle from the admin.kiey.com app bundle.
    isMyMarketBundle() {
      return process.env.VUE_APP_PUBLIC_PATH === '/my-market-app/';
    },
    // Standalone public tools (kiey.com/my-market, kiey.com/evaluation) are consumer-
    // facing pages with their own login — they must NEVER show the app's force-update
    // gate. isMyMarketBundle covers the deployed kiey.com bundle; this route check also
    // covers the admin/dev bundle so the tools are testable locally and never trapped.
    isPublicToolRoute() {
      const p = this.$route && this.$route.path;
      return p === '/evaluation' || p === '/my-market';
    },
    updateRequired() {
      if (this.isMyMarketBundle) return false;
      if (this.isPublicToolRoute) return false;
      // Never trap a user: if a reload already proved the update can't change the
      // served version, stop blocking even if we're still below the floor.
      if (this.escapedStuck) return false;
      return isUpdateRequired(this.localVersion, this.requiredVersion);
    }
  },
  mounted() {
    if (this.isMyMarketBundle) return;
    this.detectStuckReload();
    try {
      this.logoSrc = require('@/assets/favicon.svg');
    } catch (e) {
      this.logoSrc = '';
    }
    this.subscribe();
  },
  beforeDestroy() {
    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  },
  methods: {
    // Read the marker left by a previous reload(). If we came back on the same
    // local version, the "update" did nothing — the configured floor is above the
    // deployed bundle and reloading will never help — so fail open. If the version
    // changed, the update worked: clear the marker and behave normally.
    detectStuckReload() {
      try {
        const raw = sessionStorage.getItem('kiey_force_update_reload');
        if (!raw) return;
        const marker = JSON.parse(raw);
        if (marker && marker.version === this.localVersion) {
          this.escapedStuck = true;
          console.warn(
            '[ForceUpdateOverlay] Update reload returned the same version (v' +
            this.localVersion + '). The deployed bundle is below the required ' +
            'min_version — failing open so the user is not trapped.'
          );
        } else {
          sessionStorage.removeItem('kiey_force_update_reload');
        }
      } catch (e) {
        // sessionStorage unavailable (private mode / disabled) — ignore.
      }
    },
    subscribe() {
      try {
        this.unsubscribe = firestore
          .collection('app_current_version')
          .doc('web')
          .onSnapshot(snap => {
            if (!snap.exists) return;
            const data = snap.data() || {};
            this.requiredVersion = String(data.min_version || '').trim();
          }, error => {
            console.error('[ForceUpdateOverlay] version listener error:', error);
          });
      } catch (e) {
        console.error('[ForceUpdateOverlay] failed to subscribe:', e);
      }
    },
    async reload() {
      this.reloading = true;
      try {
        if ('serviceWorker' in navigator) {
          const registrations = await navigator.serviceWorker.getRegistrations();
          await Promise.all(registrations.map(r => r.unregister().catch(() => {})));
        }
        if ('caches' in window) {
          const keys = await caches.keys();
          await Promise.all(keys.map(k => caches.delete(k).catch(() => {})));
        }
      } catch (e) {
        console.warn('[ForceUpdateOverlay] cache cleanup skipped:', e);
      }
      // Record the version we're trying to escape from. If the next load comes
      // back on this same version, detectStuckReload() will fail open instead of
      // looping the user forever on an update that can't succeed.
      try {
        sessionStorage.setItem(
          'kiey_force_update_reload',
          JSON.stringify({ version: this.localVersion, at: Date.now() })
        );
      } catch (e) {
        // sessionStorage unavailable — proceed; worst case is the old loop behavior.
      }
      const url = new URL(window.location.href);
      url.searchParams.set('v', Date.now().toString());
      window.location.replace(url.toString());
    }
  }
};
</script>

<style scoped>
.force-update-overlay {
  position: fixed;
  inset: 0;
  background: rgba(6, 29, 71, 0.92);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2147483647;
  padding: 20px;
  font-family: Roboto, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.force-update-card {
  background: #fff;
  border-radius: 14px;
  max-width: 420px;
  width: 100%;
  padding: 32px 28px;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.35);
}
.force-update-logo {
  width: 56px;
  height: 56px;
  margin-bottom: 16px;
}
.force-update-title {
  font-size: 22px;
  font-weight: 600;
  color: #061D47;
  margin: 0 0 12px;
}
.force-update-text {
  font-size: 15px;
  line-height: 1.45;
  color: #333;
  margin: 0 0 12px;
}
.force-update-meta {
  font-size: 13px;
  color: #666;
  margin: 0 0 24px;
}
.force-update-meta strong {
  color: #061D47;
}
.force-update-btn {
  background: #061D47;
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 12px 28px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  min-width: 160px;
  transition: opacity 0.15s;
}
.force-update-btn:hover:not(:disabled) {
  opacity: 0.9;
}
.force-update-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
</style>
